// Checkout view of one order — /buy?order=<id>, reached from a just-created order, from
// /purchases ("Lanjutkan pembayaran"), or from a bookmarked checkout. It never CREATES an
// order (that is pages/buy/TierChoice, the one place a charge is opened); it follows the
// existing one: pending (stored QRIS + countdown + polling) → paid (activation steps) |
// failed | expired, and routes back to /buy when a fresh order is the only way forward.
// Payment capture is NEVER in-app: we only render the stored QRIS / hosted checkout link.
// Card anatomy: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx — MIT.

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { Link } from "react-router-dom";
import { Glyph } from "@/ui";
import { dataClass } from "@ahcut/ui/recipes";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "@/ui";
import { downloadQrisPng, QrisCode } from "@/components/QrisCode";
import { Loading, ErrorState } from "@/components/States";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import {
  getOrder,
  orderHasPaymentDetails,
  orderPriceBreakdown,
  type OrderPriceBreakdown,
} from "@/lib/orders";
import { MARKETING_BASE } from "@/lib/config";
import { formatCountdown, formatDateTime, formatIDR, tierLabel } from "@/lib/format";
import { ApiError } from "@/lib/api";
import { toError } from "@/lib/errors";
import { supportMailto } from "@/lib/support";
import type { Order } from "@/lib/types";
import { DetailHeader } from "@/components/DetailHeader";
import { Step, Steps } from "@/pages/buy/Steps";

const POLL_MS = 4000;
const PAGE_TITLE = "Pembayaran pesanan";

/** Order states that end the checkout (polling stops; the card settles). */
function isTerminal(status: string): boolean {
  return status === "paid" || status === "failed" || status === "expired";
}

type CheckoutView = "pending" | "paid" | "failed" | "expired";

/** Announced (polite) when polling or the deadline changes the view. */
const VIEW_ANNOUNCEMENT: Record<CheckoutView, string> = {
  pending: "Menunggu pembayaran",
  paid: "Pembayaran berhasil",
  failed: "Pembayaran gagal",
  expired: "Kode QRIS kedaluwarsa",
};

/* Fetches the order behind ?order=<id> and hands it to Checkout. The h1 renders in
   every state (ui/DESIGN.md §10). */
export function CheckoutRoute({ orderId }: { orderId: string }) {
  const order = useAsync<Order>((s) => getOrder(orderId, s), [orderId]);
  const header = (
    <DetailHeader backTo="/purchases" backLabel="Semua pembelian" title={PAGE_TITLE} />
  );
  const loading = (
    <>
      {header}
      <Loading label="Memuat pesanan…" />
    </>
  );

  if (order.loading) return loading;
  if (order.error) {
    return (
      <>
        {header}
        <ErrorState error={order.error} onRetry={order.reload} />
      </>
    );
  }
  if (!order.data) return loading;

  return <Checkout order={order.data} />;
}

function Checkout({ order }: { order: Order }) {
  const [status, setStatus] = useState(order.status);
  const [pollError, setPollError] = useState<ApiError | Error | null>(null);
  const stopped = useRef(false);
  const qrRef = useRef<HTMLDivElement>(null); // wraps QrisCode only — the PNG download reads its <svg>

  // Charge deadline (provider-reported). When it passes while the page is open,
  // flip the view locally — the server flips the row after a settlement grace, but
  // a dead QRIS must never keep LOOKING payable in the meantime. Polling continues:
  // a deadline-racing payment that still lands flips the view to paid.
  const expiresMs = order.expires_at ? new Date(order.expires_at).getTime() : null;
  const [pastExpiry, setPastExpiry] = useState(
    () => expiresMs !== null && !Number.isNaN(expiresMs) && expiresMs <= Date.now(),
  );
  useEffect(() => {
    if (expiresMs === null || Number.isNaN(expiresMs)) return;
    if (expiresMs <= Date.now()) {
      // The deadline slipped past between render (useState initializer) and this
      // effect — flip now, or the dead QRIS would keep rendering as payable.
      setPastExpiry(true);
      return;
    }
    const id = setTimeout(
      () => setPastExpiry(true),
      Math.min(expiresMs - Date.now(), 2 ** 31 - 1), // clamp: setTimeout overflows past ~24.8d
    );
    return () => clearTimeout(id);
  }, [expiresMs]);

  // Ticking countdown for the pending card. Recomputed from Date.now() each tick,
  // so background-tab interval throttling only pauses the display — it can't
  // drift the deadline. Stops when the deadline passes OR the order settles.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (expiresMs === null || Number.isNaN(expiresMs) || pastExpiry || isTerminal(status)) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresMs, pastExpiry, status]);

  const poll = useCallback(async () => {
    try {
      const latest = await getOrder(order.id);
      // Ticks overlap when a response is slow (setInterval doesn't wait): a stale
      // "pending" resolving AFTER a "paid" must neither repaint the settled card
      // nor restart polling — hence the stopped guard and the terminal no-regress.
      if (stopped.current) return latest.status;
      setStatus((prev) => (isTerminal(prev) ? prev : latest.status));
      setPollError(null); // recovered — don't leave a stale error under the pending card
      return latest.status;
    } catch (err) {
      if (!stopped.current) setPollError(toError(err));
      return null;
    }
  }, [order.id]);

  useEffect(() => {
    if (isTerminal(status)) {
      stopped.current = true; // settled — an in-flight stale response must not repaint
      return;
    }
    stopped.current = false;
    const id = setInterval(async () => {
      if (stopped.current) return;
      const s = await poll();
      if (s && isTerminal(s)) {
        stopped.current = true;
        clearInterval(id);
      }
    }, POLL_MS);
    return () => {
      stopped.current = true;
      clearInterval(id);
    };
  }, [poll, status]);

  const view: CheckoutView =
    status === "paid"
      ? "paid"
      : status === "failed"
        ? "failed"
        : status === "expired" || pastExpiry
          ? "expired"
          : "pending";

  // Fee-by-buyer: the QR bills harga + fee QRIS, so every "total" shown is the
  // number the buyer's bank app shows, never just the price. Only a still-payable
  // order (pending, with a stored QRIS or checkout link) is described as unpaid and
  // watched; a settled, expired or detail-less order only states its value.
  const price = orderPriceBreakdown(order);
  const payable = view === "pending" && orderHasPaymentDetails(order);
  const lede =
    view === "paid"
      ? `Lunas — total pembayaran ${formatIDR(price.total)}.`
      : payable
        ? "Pesanan ini belum dibayar. Statusnya diperbarui otomatis di halaman ini."
        : `Pesanan senilai ${formatIDR(order.amount)}.`;

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      <DetailHeader
        backTo="/purchases"
        backLabel="Semua pembelian"
        title={PAGE_TITLE}
        lede={lede}
      />
      <p role="status" className="sr-only">
        {VIEW_ANNOUNCEMENT[view]}
      </p>

      {view === "paid" ? (
        <PaidCard order={order} />
      ) : view === "failed" ? (
        <FailedCard orderId={order.id} />
      ) : view === "expired" ? (
        <ExpiredCard orderId={order.id} confirmedByServer={status === "expired"} />
      ) : (
        <>
          <PendingCard
            order={order}
            price={price}
            countdown={
              expiresMs !== null && !Number.isNaN(expiresMs)
                ? formatCountdown(expiresMs - nowMs)
                : null
            }
            qrRef={qrRef}
          />
          {pollError && <ErrorState error={pollError} />}
        </>
      )}
    </>
  );
}

function PendingCard({
  order,
  price,
  countdown,
  qrRef,
}: {
  order: Order;
  price: OrderPriceBreakdown;
  countdown: string | null;
  qrRef: RefObject<HTMLDivElement | null>;
}) {
  const { qris_payload, checkout_url } = order.checkout ?? {};
  const showLines = price.discount > 0 || price.fee !== null;

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Badge tone="warn" dot>
          Menunggu pembayaran
        </Badge>
        {countdown !== null && (
          <p className="text-sm text-fg-1">
            Sisa waktu <span className={cn(dataClass, "font-medium text-fg-0")}>{countdown}</span>
          </p>
        )}
      </div>

      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        {qris_payload && (
          <div ref={qrRef} className="shrink-0">
            <QrisCode payload={qris_payload} />
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/* The exact number the buyer's bank app will bill (the webhook verifies it). */}
          <div className="flex flex-col gap-1">
            <p className="text-sm text-fg-2">Total pembayaran</p>
            <p className={cn(dataClass, "text-price text-fg-0")}>{formatIDR(price.total)}</p>
          </div>

          {showLines && (
            <DescriptionList className="max-w-xs grid-cols-[minmax(0,1fr)_auto] gap-y-1.5 text-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-y-1.5">
              {price.discount > 0 && (
                <>
                  <DescriptionTerm className="mt-0">Harga awal</DescriptionTerm>
                  <DescriptionDetails className={cn(dataClass, "text-right")}>
                    {formatIDR(price.list)}
                  </DescriptionDetails>
                  <DescriptionTerm className="mt-0">Kupon {order.coupon_code}</DescriptionTerm>
                  <DescriptionDetails className={cn(dataClass, "text-right")}>
                    −{formatIDR(price.discount)}
                  </DescriptionDetails>
                </>
              )}
              <DescriptionTerm className="mt-0">Harga</DescriptionTerm>
              <DescriptionDetails className={cn(dataClass, "text-right")}>
                {formatIDR(order.amount)}
              </DescriptionDetails>
              {price.fee !== null && (
                <>
                  <DescriptionTerm className="mt-0">Fee QRIS</DescriptionTerm>
                  <DescriptionDetails className={cn(dataClass, "text-right")}>
                    {formatIDR(price.fee)}
                  </DescriptionDetails>
                </>
              )}
            </DescriptionList>
          )}

          {qris_payload ? (
            <>
              <p className="text-sm text-fg-1">
                Bayar dari perangkat ini? Unduh kode QR, lalu di aplikasi pembayaran pilih bayar
                QRIS dan unggah gambarnya dari galeri.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="quiet"
                  size="sm"
                  onClick={() => void downloadQrisPng(qrRef.current, `ahcut-qris-${order.id}.png`)}
                >
                  <Glyph name="download" />
                  Unduh kode QR
                </Button>
                {checkout_url && (
                  <Button asChild variant="quiet" size="sm">
                    <a href={checkout_url} target="_blank" rel="noreferrer">
                      Buka halaman pembayaran
                      <Glyph name="external" />
                    </a>
                  </Button>
                )}
              </div>
            </>
          ) : checkout_url ? (
            <div>
              <Button asChild variant="secondary">
                <a href={checkout_url} target="_blank" rel="noreferrer">
                  Buka halaman pembayaran
                  <Glyph name="external" />
                </a>
              </Button>
            </div>
          ) : (
            // Orders created before checkout persistence (migration 0005) have no stored
            // QRIS, and it cannot be re-fetched from the provider. The server also withholds
            // `checkout` from the pending order of an already-entitled user — paying it would
            // charge twice — so this branch never promises a QRIS, only a fresh start.
            <p className="text-sm text-fg-1">
              Detail pembayaran untuk pesanan ini tidak tersedia lagi. Pesanan ini tidak ditagihkan
              dan akan kedaluwarsa sendiri — <Link to="/buy">mulai pesanan baru</Link> kalau masih
              mau melanjutkan.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-line pt-4 text-sm text-fg-2">
        {order.expires_at && (
          <p>
            Berlaku sampai{" "}
            <span className="font-medium text-fg-1">{formatDateTime(order.expires_at)}</span>.
            Setelah itu kode QRIS tidak bisa dibayar lagi.
          </p>
        )}
        <p>
          Order ID: <code>{order.id}</code>
        </p>
        {orderHasPaymentDetails(order) && (
          <p>
            Dengan membayar kamu menyetujui{" "}
            <a href={`${MARKETING_BASE}/syarat`} target="_blank" rel="noopener noreferrer">
              Syarat
            </a>{" "}
            dan{" "}
            <a href={`${MARKETING_BASE}/refund`} target="_blank" rel="noopener noreferrer">
              Kebijakan Refund
            </a>
            .
          </p>
        )}
      </div>
    </Card>
  );
}

function PaidCard({ order }: { order: Order }) {
  return (
    <Card>
      <div>
        <Badge tone="ok" dot>
          Lunas
        </Badge>
      </div>
      <CardHeader>
        <CardTitle>Lisensi {tierLabel(order.tier)} sudah aktif di akunmu</CardTitle>
        <p className="text-fg-1">Tinggal pasang di perangkatmu.</p>
      </CardHeader>

      <Steps>
        <Step
          number={1}
          title={
            <a href={`${MARKETING_BASE}/unduh`} target="_blank" rel="noopener noreferrer">
              Unduh &amp; pasang aplikasi
            </a>
          }
        >
          <p className="text-sm text-fg-2">Sudah terpasang? Lanjut ke langkah 2.</p>
        </Step>
        <Step number={2} title="Aktifkan di aplikasi">
          <p className="text-fg-1">
            Pilih <strong>License…</strong> di menu logo ahcut, lalu tekan <strong>I have Pro · Sign in</strong>.
            Lisensi terpasang otomatis.
          </p>
          <div>
            <Button asChild variant="primary">
              <a href="ahcut://activate">Buka ahcut &amp; aktifkan</a>
            </Button>
          </div>
          <p className="text-sm text-fg-2">
            Aplikasi tidak terbuka? Pastikan sudah terpasang, atau aktivasi manual di halaman{" "}
            <Link to="/license">Lisensi</Link>.
          </p>
        </Step>
      </Steps>

      <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-line pt-4 text-sm">
        <Link to="/license">Aktivasi manual (Machine ID)</Link>
        <Link to={`/purchases/${encodeURIComponent(order.id)}`}>Lihat kwitansi</Link>
      </div>
    </Card>
  );
}

function FailedCard({ orderId }: { orderId: string }) {
  return (
    <Card tone="danger" role="alert">
      <div>
        <Badge tone="crit" dot>
          Pembayaran gagal
        </Badge>
      </div>
      <p className="text-fg-1">Pembayaran tidak selesai atau ditolak. Tidak ada yang ditagihkan.</p>
      <div>
        <Button asChild variant="primary">
          <Link to="/buy">Coba pesan lagi</Link>
        </Button>
      </div>
      <p className="text-sm text-fg-2">
        Merasa sudah membayar?{" "}
        <a href={supportMailto(`Pembayaran gagal — Order ${orderId}`)}>Hubungi dukungan</a>.
      </p>
    </Card>
  );
}

/* The QRIS deadline passed. While the row is still 'pending' (local flip; the server
   keeps a ~30-min settlement grace) polling CONTINUES — a payment that raced the
   deadline still lands and flips this to paid, so the copy must not yet claim nothing
   was charged. A server-flipped 'expired' means the provider reported no payment for
   the whole grace: then the no-charge claim is safe and polling stops. */
function ExpiredCard({ orderId, confirmedByServer }: { orderId: string; confirmedByServer: boolean }) {
  return (
    <Card role="alert">
      <div>
        <Badge tone="neutral" dot>
          Kedaluwarsa
        </Badge>
      </div>
      <p className="text-fg-1">
        Kode QRIS pesanan ini sudah kedaluwarsa dan tidak bisa dibayar lagi.{" "}
        {confirmedByServer
          ? "Tidak ada yang ditagihkan."
          : "Kalau kamu sempat membayar tepat sebelum tenggat, status akan diperbarui otomatis dalam beberapa menit — selain itu tidak ada yang ditagihkan."}
      </p>
      <div>
        <Button asChild variant="primary">
          <Link to="/buy">Buat pesanan baru</Link>
        </Button>
      </div>
      <p className="text-sm text-fg-2">
        Merasa sudah membayar?{" "}
        <a href={supportMailto(`Pesanan kedaluwarsa — Order ${orderId}`)}>Hubungi dukungan</a>.
      </p>
    </Card>
  );
}
